import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Clock, BarChart3, Briefcase, Users, Tag, LogOut, Building2, ChevronDown, Check, Settings, CalendarRange, Layers } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { toast } from "sonner";

const linkBase =
  "flex items-center gap-3 px-4 py-2 text-sm rounded-md mx-2 transition-colors";
const inactive = "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
const active = "bg-[#e8f1f8] text-[#0b2c47] font-medium";

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}

function OrgSwitcher({ orgs, activeOrg, onSwitched }) {
  const [open, setOpen] = useState(false);
  const activeName = activeOrg?.name || "Workspace";
  const activeId = activeOrg?.id;

  const handleSwitch = async (orgId) => {
    if (orgId === activeId) {
      setOpen(false);
      return;
    }
    try {
      await api.post("/organisations/switch", { organisation_id: orgId });
      setOpen(false);
      toast.success("Switched organisation");
      onSwitched?.();
      // Hard reload to refresh all org-scoped data cleanly
      window.location.reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Users belonging to a single org see just the org name (no dropdown).
  if (orgs.length <= 1) {
    return (
      <div className="px-5 py-5 border-b border-slate-200" data-testid="org-display">
        <div className="text-base font-semibold tracking-tight leading-tight truncate"
             style={{ fontFamily: "Manrope" }}>
          {activeName}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 leading-tight mt-0.5">
          Time Tracker
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full px-5 py-5 border-b border-slate-200 text-left hover:bg-black/5 transition-colors group"
          data-testid="org-switcher-trigger"
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-3.5 w-3.5 rounded-full border border-white/40 shrink-0"
              style={{ background: activeOrg?.color || "#0d9488" }}
              data-testid="org-switcher-swatch"
            />
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold tracking-tight leading-tight truncate"
                   style={{ fontFamily: "Manrope" }}>
                {activeName}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 leading-tight mt-0.5">
                Time Tracker
              </div>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="w-64 p-1.5" data-testid="org-switcher-menu">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pt-1.5 pb-1">
          Switch organisation
        </div>
        <div className="max-h-64 overflow-y-auto">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => handleSwitch(o.id)}
              className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-slate-100"
              data-testid={`org-switcher-item-${o.id}`}
            >
              <span
                className="inline-block h-3 w-3 rounded-full border border-slate-200 shrink-0"
                style={{ background: o.color || "#0d9488" }}
              />
              <span className="flex-1 truncate">{o.name}</span>
              {o.id === activeId && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
            </button>
          ))}
        </div>
        <div className="border-t border-slate-100 mt-1 pt-1">
          <NavLink
            to="/organisations"
            onClick={() => setOpen(false)}
            className="block w-full text-left px-2 py-1.5 rounded text-sm text-[#145c8c] hover:bg-[#e8f1f8]"
            data-testid="org-switcher-manage"
          >
            Manage organisations…
          </NavLink>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Convert a 3/6-char hex colour to `rgba(r,g,b,alpha)` for a light tint.
 * Falls back to a neutral slate tint when the input isn't parseable.
 */
function hexToRgba(hex, alpha = 0.12) {
  if (!hex || typeof hex !== "string") return `rgba(148,163,184,${alpha})`;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return `rgba(148,163,184,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return `rgba(148,163,184,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/organisations");
        if (!cancelled) setOrgs(data || []);
      } catch (e) {
        // silent — sidebar still renders without tint
      }
    })();
    return () => { cancelled = true; };
  }, [user?.active_organisation_id, user?.organisation_id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const activeId = user?.active_organisation_id || user?.organisation_id;
  const activeOrg = orgs.find((o) => o.id === activeId);
  const tint = hexToRgba(activeOrg?.color, 0.10);

  return (
    <aside className="w-60 shrink-0 border-r border-slate-200 flex flex-col h-screen sticky top-0"
           style={{ background: tint }}
           data-testid="sidebar">
      {/* Org switcher / brand */}
      <OrgSwitcher orgs={orgs} activeOrg={activeOrg} />

      {/* Nav groups */}
      <div className="flex-1 overflow-y-auto py-4 space-y-6">
        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-5">
            Track
          </div>
          <NavItem to="/timer" icon={Clock} label="Timer" testId="nav-timer" />
        </div>

        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-5">
            Analyse
          </div>
          <NavItem to="/reports" icon={BarChart3} label="Reports" testId="nav-reports" />
          {user?.role === "admin" && (
            <NavItem to="/team-calendar" icon={CalendarRange} label="Team Calendar" testId="nav-team-calendar" />
          )}
        </div>

        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-5">
            Manage
          </div>
          <NavItem to="/clients" icon={Briefcase} label="Clients" testId="nav-clients" />
          <NavItem to="/tags" icon={Layers} label="Categories" testId="nav-tags" />
          <NavItem to="/activities" icon={Tag} label="Activities" testId="nav-activities" />
          {user?.role === "admin" && (
            <>
              <NavItem to="/members" icon={Users} label="Team Members" testId="nav-members" />
              <NavItem to="/organisations" icon={Building2} label="Organisations" testId="nav-organisations" />
            </>
          )}
        </div>

        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-5">
            Account
          </div>
          <NavItem to="/preferences" icon={Settings} label="Preferences" testId="nav-preferences" />
        </div>
      </div>

      {/* Footer profile */}
      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
               style={{ background: user?.color || "#145c8c" }}>
            {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
              {user?.name || user?.email}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">
              {user?.role}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900"
            title="Sign out"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
